import React from 'react';
import { useTranslation } from 'react-i18next';

import { Route, useNavigation } from '@react-navigation/native';
import { NativeStackScreenProps } from '@react-navigation/native-stack';

import { PaymentErrorModalContent } from '@/components/PaymentErrorModalContent';
import { Screen } from '@/components/ScreenComponent';
import BetrAnalytics from '@/feature/analytics/analytics';
import { AnalyticsEvent } from '@/feature/analytics/constants';
import { popToRipsGameWebView } from '@/feature/lobby-rips/utils/navigation';
import { kycVerifiedSelector, useAuthUserConfig } from '@/hooks/use-auth-user-config';
import { useHasSocialWallet } from '@/hooks/use-has-social-wallet';
import { usePreventAndroidBackNavigation } from '@/hooks/use-prevent-android-back-navigation';
import { useRootNavigation } from '@/hooks/use-root-navigation';
import { FailedDepositNavigationProp } from '@/navigation/deposit/types';
import {
    findProductBetSlip,
    isRouteInHistory,
    navigateToKycSelector,
    popToHome,
    popToProductBetSlip,
} from '@/navigation/navigation';
import { DepositStackParamList } from '@/navigation/types';

import { setDepositAmount, useDepositStatusStore } from '../hooks/use-deposit-status-store';
import { useTrackFirstDepositEvent } from '../hooks/use-track-first-deposit-event';
import { PayPalStatus } from '../utils';

type ScreenProps = NativeStackScreenProps<DepositStackParamList, 'FailedDepositModal'>;

export const FailedDeposit = ({ route }: ScreenProps) => {
    const { paymentProvider, errorCode = '', showContactSupport = false, description } = route.params;
    const selectedAmount = useDepositStatusStore(state => state.selectedAmount);
    const navigation = useNavigation<FailedDepositNavigationProp>();

    const rootNavigation = useRootNavigation();

    const depositRoute = navigation.getState()?.routes.find(localRoute => localRoute.name === 'DepositScreen') as Route<
        'DepositScreen',
        DepositStackParamList['DepositScreen']
    >;
    const { t } = useTranslation();
    const hasSocialWallet = useHasSocialWallet();

    const { data: isKycVerified } = useAuthUserConfig({ select: kycVerifiedSelector, refetchOnMount: 'always' });

    usePreventAndroidBackNavigation();
    useTrackFirstDepositEvent(PayPalStatus.ERROR, paymentProvider, selectedAmount, errorCode);

    const handleClose = () => {
        BetrAnalytics.trackProductEvent(AnalyticsEvent.DEPOSIT_FAILURE_ABORT, { method: paymentProvider });

        // If the user is not verified, pressing 'X' should take them back to the KYC selector.
        if (!isKycVerified) {
            navigateToKycSelector(rootNavigation);
            return;
        }

        const betSlipStack = findProductBetSlip(rootNavigation);

        if (betSlipStack) {
            popToProductBetSlip(rootNavigation, betSlipStack);
            return;
        }

        if (isRouteInHistory(rootNavigation, 'RipsGameWebView')) {
            popToRipsGameWebView(navigation);
            return;
        }

        if (depositRoute?.params?.shouldPopToLobby) {
            popToHome(rootNavigation, 'lobby');
            return;
        }

        popToHome(rootNavigation, 'account');
    };

    const handlePrimaryButtonPress = () => {
        BetrAnalytics.trackProductEvent(AnalyticsEvent.DEPOSIT_FAILURE_RETRY, { method: paymentProvider });
        setDepositAmount(selectedAmount);
        if (!isKycVerified) {
            navigateToKycSelector(rootNavigation);
            return;
        }

        if (hasSocialWallet) {
            navigation.popTo('CoinPackageScreen');
        } else {
            navigation.popTo('DepositScreen', {});
        }
    };

    return (
        <Screen edges={['top', 'bottom']}>
            <PaymentErrorModalContent
                transactionType="deposit"
                onClose={handleClose}
                errorCode={errorCode}
                selectedAmount={selectedAmount}
                primaryButton={t('retry')}
                handlePrimaryButtonPress={handlePrimaryButtonPress}
                showSecondaryButton={false}
                showContactSupport={showContactSupport}
                handleSupportEmailPress={() => {
                    BetrAnalytics.trackProductEvent(AnalyticsEvent.DEPOSIT_FAILURE_SUPPORT_REQUEST, {
                        method: paymentProvider,
                    });
                }}
                invokingScreen={route.name}
                paymentMethod={paymentProvider}
                description={description}
            />
        </Screen>
    );
};
